/**
 * Shared autocomplete label helpers.
 *
 * Kept here (rather than inside a page component) so both the homepage
 * HeroSearchBar and the AlgoliaSearchPageContent location field can
 * import a single canonical implementation and stay in sync.
 */

/**
 * Build the location input value from an autocomplete suggestion.
 *
 * Country-level results (no city, no state) must use the full displayName
 * (e.g. "Norway") rather than the compact city+state+code label, which
 * degrades to just the ISO code ("NO") when both city and state are absent.
 * Sending "NO" to the search page causes it to geocode the bare code as a
 * city name, get no `isCountry: true` result, and return zero cars.
 *
 * City/region results (city or state present) still produce the usual
 * "Oslo, Oslo, NO" compact form.
 *
 * Task #7002 — the API's explicit `isCountry` flag (when present) is
 * authoritative; the empty-city/state inference remains only as a fallback.
 * Detection is delegated to `inferSuggestionIsCountry` so the label and the
 * search page's geocode hint can never disagree.
 *
 * Exported so it can be covered by unit tests.
 */
export function pickSuggestionLabel(s: {
  displayName: string;
  city: string;
  state: string;
  countryCode: string;
  /** Explicit country-level flag from /api/address/search (task #7002). */
  isCountry?: boolean;
}): string {
  if (inferSuggestionIsCountry(s)) return s.displayName;
  return [s.city, s.state, s.countryCode].filter(Boolean).join(", ") || s.displayName;
}

/**
 * Decide whether an autocomplete suggestion represents an entire country.
 *
 * The API's `isCountry` flag is authoritative when present, but it can be
 * absent (or false) on responses that are clearly country-level — no city
 * and no state. Without this fallback the search page would treat such a
 * pick as a city, geocode a radius around the country centroid, and return
 * zero cars. Mirrors the "no city, no state ⇒ country" heuristic that
 * pickSuggestionLabel already uses for the display label, so the label and
 * the geocode hint can never disagree.
 *
 * Exported so it can be covered by unit tests.
 */
export function inferSuggestionIsCountry(s: {
  city: string;
  state: string;
  isCountry?: boolean;
}): boolean {
  if (s.isCountry) return true;
  return !s.city && !s.state;
}

/** The URL marker records an explicit country pick, never an inferred text search. */
export function selectedCountryCode(location: string | undefined, code: string | undefined): string | null {
  if (!location?.trim() || !code || !/^[A-Z]{2}$/.test(code)) return null;
  return code;
}
